<template>
  <!--
    Cinematic feature stage:
      • The stage is explicitly N × 100dvh tall (one viewport of scroll per
        scene). A sticky child fills the viewport and stays pinned for the
        full duration.
      • N invisible triggers are absolutely positioned at evenly spaced
        scroll offsets. An IntersectionObserver with a tiny centered
        rootMargin band fires whenever a trigger crosses viewport center,
        and we set that index as active.
      • All transitions are opacity + transform + background-color (GPU
        compositor only) — phone slides in from the side, text fades up,
        background tint shifts, accent glow swaps.
  -->
  <section
    ref="rootEl"
    class="feature-stage"
    :class="[
      `accent-${currentScene.accent}`,
      { 'stage-offscreen': !stageVisible },
    ]"
    :style="{ '--scene-count': scenes.length }"
  >
    <!-- Sticky visible layer — pins for the full stage height -->
    <div class="stage-sticky">
      <!-- 5 stacked accent backgrounds — each is the original FeatureScene
           vertical gradient (white plateaus + soft color peak in the middle
           of the viewport). Only the active one is opaque; the rest are
           cross-faded out via opacity. -->
      <div class="stage-bg-layers">
        <div
          v-for="(s, i) in scenes"
          :key="`bg-${i}`"
          class="stage-bg-layer"
          :class="[`bg-${s.accent}`, { active: i === activeIdx }]"
        ></div>
      </div>

      <!-- Ambient glows: 5 stacked, only the active accent's glow is shown -->
      <div class="stage-glow-clip">
        <div
          v-for="(s, i) in scenes"
          :key="`glow-${i}`"
          class="stage-glow"
          :class="[
            `glow-${s.accent}`,
            { active: i === activeIdx, left: s.flip },
          ]"
        ></div>
      </div>

      <div class="stage-inner" :class="{ reverse: currentScene.flip }">
        <!-- Text side: all blocks share one grid cell, only active is visible -->
        <div class="stage-text">
          <div
            v-for="(s, i) in scenes"
            :key="`text-${i}`"
            class="stage-text-block"
            :class="[`accent-${s.accent}`, { active: i === activeIdx }]"
          >
            <div class="stage-eyebrow">{{ s.eyebrow }}</div>
            <h2 class="stage-headline" v-html="s.headline"></h2>
            <p v-if="s.caption" class="stage-caption">{{ s.caption }}</p>
          </div>
        </div>

        <!-- Phone side: same overlay pattern, slides in from side.
             On mobile, `content-visibility: auto` on inactive blocks
             (see CSS below) tells iOS Safari to skip rendering their
             subtrees until they're near the viewport, dropping the
             compositor cost of 4 inactive FeatureMockups at a time. -->
        <div class="stage-visual">
          <div
            v-for="(s, i) in scenes"
            :key="`visual-${i}`"
            class="stage-visual-block"
            :class="{
              active: i === activeIdx,
              before: i < activeIdx,
              after: i > activeIdx,
            }"
          >
            <FeatureMockup :type="s.mockup" :active="i === activeIdx" />
          </div>
        </div>
      </div>

      <!-- Scene progress — 5 monochrome pills; upcoming = subtle
           gray, past = darker gray, active = widest + solid black.
           Clickable: tap a pill to jump directly to that scene. -->
      <div class="stage-progress" role="tablist" aria-label="Feature sections">
        <button
          v-for="(s, i) in scenes"
          :key="`dot-${i}`"
          type="button"
          role="tab"
          :aria-selected="i === activeIdx"
          :aria-label="`Go to ${s.eyebrow}`"
          class="stage-progress-dot"
          :class="{ active: i === activeIdx, past: i < activeIdx }"
          @click="goToScene(i)"
        ></button>
      </div>
    </div>

    <!-- Invisible scroll triggers — absolutely positioned, evenly spaced.
         Each one fires the matching scene as it crosses viewport center. -->
    <div
      v-for="(s, i) in scenes"
      :key="`trigger-${i}`"
      :ref="
        (el) => {
          if (el) triggerEls[i] = el;
        }
      "
      class="stage-trigger"
      :style="{ '--i': i }"
      :data-idx="i"
      aria-hidden="true"
    />
  </section>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import FeatureMockup from './FeatureMockup.vue';

export default {
  name: 'FeatureStage',
  components: { FeatureMockup },
  props: {
    scenes: { type: Array, required: true },
  },
  setup(props) {
    const rootEl = ref(null);
    const triggerEls = ref([]);
    const activeIdx = ref(0);
    const stageVisible = ref(true);
    let observer = null;
    let visibilityObs = null;

    const currentScene = computed(
      () => props.scenes[activeIdx.value] || props.scenes[0],
    );

    // Guard flag — true for a short window after a pill click so
    // the observer ignores any intermediate trigger intersections
    // fired by the instant scroll. Without this, browsers that
    // don't honor `behavior: 'instant'` perfectly (iOS, some
    // Android Chrome) can briefly re-trigger intermediate scenes
    // during the scroll step, causing a flicker.
    let suppressObserver = false;
    let suppressTimer = null;

    // Click handler for the progress pills — jumps directly to the
    // target scene. Because transitions only fire when opacity actually
    // changes, only the outgoing + target blocks animate. The observer
    // is muted for 600 ms while the scroll settles.
    const goToScene = (i) => {
      if (i === activeIdx.value) return;
      const trigger = triggerEls.value[i];
      if (!trigger || typeof trigger.scrollIntoView !== 'function') return;
      suppressObserver = true;
      activeIdx.value = i;
      trigger.scrollIntoView({ block: 'center', behavior: 'instant' });
      if (suppressTimer) clearTimeout(suppressTimer);
      suppressTimer = setTimeout(() => {
        suppressObserver = false;
      }, 600);
    };

    onMounted(() => {
      if (!('IntersectionObserver' in window) || !triggerEls.value.length) {
        return;
      }

      // On narrow viewports, momentum scrolling on iOS Safari can skip
      // past a 10% activation band between frames (the "scenes don't
      // animate" symptom). Widen the band on mobile so fast flicks
      // still register.
      const isNarrow =
        typeof window !== 'undefined' &&
        window.matchMedia('(max-width: 767px)').matches;

      // rootMargin shrinks the viewport to a thin centered band — a trigger
      // is "intersecting" only when its top edge is in that band, which
      // gives us a single deterministic activation point per scene with no
      // jitter at the boundary.
      observer = new IntersectionObserver(
        (entries) => {
          if (suppressObserver) return;
          entries.forEach((e) => {
            if (e.isIntersecting) {
              const idx = parseInt(e.target.dataset.idx, 10);
              if (!Number.isNaN(idx) && idx !== activeIdx.value) {
                activeIdx.value = idx;
              }
            }
          });
        },
        {
          rootMargin: isNarrow ? '-35% 0px -35% 0px' : '-45% 0px -45% 0px',
          threshold: 0,
        },
      );

      triggerEls.value.forEach((el) => el && observer.observe(el));

      // Whole-stage visibility: release the blur(120px) glow buffer
      // and bg-layer composited textures when scrolled offscreen.
      if (rootEl.value) {
        visibilityObs = new IntersectionObserver(
          ([entry]) => {
            stageVisible.value = entry.isIntersecting;
          },
          { threshold: 0 },
        );
        visibilityObs.observe(rootEl.value);
      }
    });

    onBeforeUnmount(() => {
      if (observer) observer.disconnect();
      if (visibilityObs) visibilityObs.disconnect();
    });

    return {
      rootEl,
      triggerEls,
      activeIdx,
      currentScene,
      stageVisible,
      goToScene,
    };
  },
};
</script>

<style scoped>
/* ─── Stage shell ─────────────────────────────────────────── */
.feature-stage {
  position: relative;
  width: 100%;
  /* Total scroll length = one viewport per scene. The sticky child stays
     pinned for the entire duration. */
  height: calc(var(--scene-count, 5) * 100vh);
  height: calc(var(--scene-count, 5) * 100dvh);
  /* White base — the per-scene gradient layers paint the accent on top */
  background-color: #ffffff;
}
/* Offscreen: release the blur(120px) glow buffer and bg-layer composited
   textures. User can't see them when the stage is scrolled out of view,
   and Safari otherwise keeps re-rasterizing them. */
.stage-offscreen .stage-glow,
.stage-offscreen .stage-bg-layer {
  visibility: hidden;
}
/* Drop the filter entirely while offscreen so the blur buffer is
   released from the compositor cache, not just hidden. Re-applies
   automatically when the .stage-offscreen class comes off. */
.stage-offscreen .stage-glow {
  filter: none;
}

/* ─── Sticky visible layer — fills viewport, stays pinned ─── */
.stage-sticky {
  position: sticky;
  top: 0;
  width: 100%;
  height: 100vh;
  /* svh = small-viewport-height — locked to URL-bar-visible state. Using
     svh here (instead of dvh) stops the sticky child from resizing when
     iOS Safari shows/hides the URL bar, which prevents the trigger band
     from drifting mid-scroll. */
  height: 100svh;
  /* Bottom padding uses whichever is larger: the existing clamp OR the
     iOS safe-area inset. Using max() (not addition) means we don't
     double-pad — the phone mockup keeps its vertical room. */
  padding: clamp(2rem, 6vw, 4.5rem) clamp(1.25rem, 5vw, 3rem)
    max(clamp(1.5rem, 4vw, 4rem), env(safe-area-inset-bottom, 0px));
  /* border-box so the padding doesn't push the sticky past 100vh and
     prevent it from actually pinning to the viewport */
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  z-index: 1;
}

/* ─── Background layers — 5 stacked accent gradients ─────── */
/* Each layer mirrors the original FeatureScene gradient (white plateau →
   soft color peak in the viewport center → white plateau). Only the
   active layer is opaque; the rest cross-fade out. The peak ALWAYS sits
   in the middle of the viewport because the layer fills the sticky
   container, which is exactly 100dvh tall. */
.stage-bg-layers {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}
.stage-bg-layer {
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity 1200ms cubic-bezier(0.4, 0, 0.2, 1);
  will-change: opacity;
}
.stage-bg-layer.active {
  opacity: 1;
}

.bg-teal {
  background: linear-gradient(
    180deg,
    #ffffff 0%,
    #ffffff 28%,
    #f4fdfb 38%,
    #dcf6ee 55%,
    #f4fdfb 72%,
    #ffffff 84%,
    #ffffff 100%
  );
}
.bg-blue {
  background: linear-gradient(
    180deg,
    #ffffff 0%,
    #ffffff 28%,
    #f3f8ff 38%,
    #dee9ff 55%,
    #f3f8ff 72%,
    #ffffff 84%,
    #ffffff 100%
  );
}
.bg-coral {
  background: linear-gradient(
    180deg,
    #ffffff 0%,
    #ffffff 28%,
    #fff7f0 38%,
    #ffe6d2 55%,
    #fff7f0 72%,
    #ffffff 84%,
    #ffffff 100%
  );
}
.bg-amber {
  background: linear-gradient(
    180deg,
    #ffffff 0%,
    #ffffff 28%,
    #fffceb 38%,
    #fef0c8 55%,
    #fffceb 72%,
    #ffffff 84%,
    #ffffff 100%
  );
}
.bg-violet {
  background: linear-gradient(
    180deg,
    #ffffff 0%,
    #ffffff 28%,
    #f6f3ff 38%,
    #e4dcff 55%,
    #f6f3ff 72%,
    #ffffff 84%,
    #ffffff 100%
  );
}

/* ─── Scene progress pills ──────────────────────────────────
   5 monochrome segments. Upcoming = 12% black; past = 35% black
   (shows direction of travel); active = 100% black + 2× wider.
   Intentionally neutral so the colorful scenes stay the star. */
.stage-progress {
  position: absolute;
  /* Sit above iOS Safari's bottom toolbar + home indicator. The max()
     keeps the desktop offset on larger screens and uses the safe-area
     inset directly on phones — no extra padding stacked on top so the
     phone mockup keeps its room. */
  bottom: max(clamp(1.5rem, 3.5vh, 2.5rem), env(safe-area-inset-bottom, 0px));
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 2;
}
.stage-progress-dot {
  /* Reset button defaults — the pill is a <button> so it's keyboard-
     focusable and screen readers can navigate scenes. */
  appearance: none;
  border: 0;
  padding: 0;
  font: inherit;
  cursor: pointer;
  display: block;
  width: 22px;
  height: 3px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.12);
  transition: width 700ms cubic-bezier(0.65, 0, 0.35, 1),
    background-color 700ms cubic-bezier(0.65, 0, 0.35, 1), transform 200ms ease;
  /* Widen the tappable area without growing the visual bar. */
  position: relative;
}
.stage-progress-dot::before {
  content: '';
  position: absolute;
  inset: -14px -4px;
}
.stage-progress-dot:hover {
  background: rgba(15, 23, 42, 0.45);
}
.stage-progress-dot:focus-visible {
  outline: 2px solid rgba(15, 23, 42, 0.65);
  outline-offset: 4px;
}
.stage-progress-dot.past {
  background: rgba(15, 23, 42, 0.35);
}
.stage-progress-dot.active {
  width: 44px;
  background: rgba(15, 23, 42, 0.9);
}

/* ─── Scroll triggers — invisible, one per scene ──────────── */
.stage-trigger {
  position: absolute;
  left: 0;
  right: 0;
  height: 100vh;
  /* Match the sticky child's svh sizing so trigger offsets and the
     sticky viewport stay synchronized across URL-bar changes. */
  height: 100svh;
  /* Stagger each trigger one viewport apart so its top edge hits the
     centered rootMargin band exactly when its scene should activate. */
  top: calc(var(--i, 0) * 100vh);
  top: calc(var(--i, 0) * 100svh);
  pointer-events: none;
  visibility: hidden;
}

/* ─── Ambient glows — 5 stacked, only the active accent shown ─ */
.stage-glow-clip {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
}
.stage-glow {
  position: absolute;
  width: 75vw;
  height: 75vw;
  max-width: 760px;
  max-height: 760px;
  border-radius: 50%;
  top: 15%;
  right: -10%;
  opacity: 0;
  pointer-events: none;
  transition: opacity 1200ms cubic-bezier(0.4, 0, 0.2, 1);
  will-change: opacity;
}
/* Desktop: 120px blur filter for the signature hazy accent glow.
   Mobile: skip the filter — 5 stacked × 760px × 120px blur = the
   single biggest GPU buffer draw on the page. The accent colors
   (defined per-class via radial-gradient below) are overridden on
   mobile with a gradual-falloff gradient that produces the same
   visual bloom via 2D painting. */
@media (min-width: 768px) {
  .stage-glow {
    filter: blur(120px);
  }
}
.stage-glow.left {
  right: auto;
  left: -10%;
}
.stage-glow.active {
  opacity: 0.5;
}

.glow-teal {
  background: radial-gradient(circle, rgba(13, 148, 136, 0.4), transparent 65%);
}
.glow-blue {
  background: radial-gradient(
    circle,
    rgba(59, 130, 246, 0.38),
    transparent 65%
  );
}
.glow-coral {
  background: radial-gradient(
    circle,
    rgba(249, 115, 22, 0.35),
    transparent 65%
  );
}
.glow-amber {
  background: radial-gradient(circle, rgba(253, 173, 0, 0.42), transparent 65%);
}
.glow-violet {
  background: radial-gradient(
    circle,
    rgba(124, 58, 237, 0.38),
    transparent 65%
  );
}

/* Mobile: true gaussian blur via pre-rasterized SVG mask, tinted
   per-accent with background-color. Browser rasterizes the SVG
   once and caches it; each accent change only swaps the tint
   color, which is free. Matches desktop filter: blur(120px)
   pixel-for-pixel without the per-frame GPU blur pass. */
@media (max-width: 767px) {
  .stage-glow {
    mask-image: url('~@/assets/ambient/blur-mask.svg');
    -webkit-mask-image: url('~@/assets/ambient/blur-mask.svg');
    mask-size: 100% 100%;
    -webkit-mask-size: 100% 100%;
    mask-repeat: no-repeat;
    -webkit-mask-repeat: no-repeat;
    mask-position: center;
    -webkit-mask-position: center;
    border-radius: 0;
  }
  .glow-teal {
    background: rgba(13, 148, 136, 0.4);
  }
  .glow-blue {
    background: rgba(59, 130, 246, 0.38);
  }
  .glow-coral {
    background: rgba(249, 115, 22, 0.35);
  }
  .glow-amber {
    background: rgba(253, 173, 0, 0.42);
  }
  .glow-violet {
    background: rgba(124, 58, 237, 0.38);
  }
}

/* ─── Inner two-column layout (mirrors FeatureScene) ──────── */
/* No height/justify-content here — the inner block sizes to its content
   and the .stage-sticky parent (which is `display:flex; align-items:center;
   justify-content:center`) centers it inside the viewport. This matches
   the original FeatureScene exactly. */
/* Centered, vertical layout on every viewport — matches the rest of the
   deck (PROBLEM, MARKET, etc.) which all use a single centered column.
   The horizontal split made desktop feel lopsided next to those slides. */
.stage-inner {
  position: relative;
  z-index: 1;
  max-width: 1180px;
  width: 100%;
  margin: 0 auto;
  padding: 0 clamp(1rem, 3vw, 2rem);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: clamp(1.25rem, 3vw, 2.5rem);
}
/* Desktop: side-by-side row layout (text + phone), like the landing page */
@media (min-width: 900px) {
  .stage-inner {
    flex-direction: row;
    gap: clamp(2rem, 5vw, 4rem);
    align-items: center;
    justify-content: space-between;
  }

  /* `flip: true` puts phone on the left, text on the right. */
  .stage-inner.reverse {
    flex-direction: row-reverse;
  }
}

/* ─── Text side — grid stack so all blocks share one cell ─── */
.stage-text {
  min-width: 0;
  width: 100%;
  max-width: 38rem;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto;
  align-items: center;
  justify-items: center;
  text-align: center;
}
/* Desktop: left-align text since it sits beside the phone */
@media (min-width: 900px) {
  .stage-text {
    flex: 1 1 50%;
    max-width: 38rem;
    justify-items: start;
    text-align: left;
  }
  .stage-text-block {
    align-items: flex-start;
  }
}
@media (min-width: 1280px) {
  .stage-text {
    max-width: 42rem;
  }
}

/* Asymmetric cross-fade via transitions (not animations).
   Using transitions means when a block goes from .before to
   .after (or vice versa) with opacity already at 0, no
   transition fires — the property didn't change. That's crucial
   for pill-jump behavior: jumping 0 → 4 shouldn't re-animate
   blocks 1-3 (which were inactive and stay inactive).
   Timing: outgoing fades fast (400ms), incoming fades in slower
   (900ms). Outgoing is gone before incoming fully arrives, so
   the two headlines never overlap at readable opacity. */
.stage-text-block {
  grid-column: 1;
  grid-row: 1;
  opacity: 0;
  transform: translateY(14px);
  pointer-events: none;
  transition: opacity 400ms cubic-bezier(0.4, 0, 1, 1),
    transform 500ms cubic-bezier(0.4, 0, 1, 1);
  will-change: opacity, transform;
}
.stage-text-block.active {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
  transition: opacity 900ms cubic-bezier(0.25, 0.1, 0.25, 1),
    transform 1100ms cubic-bezier(0.25, 0.1, 0.25, 1);
}

/* ─── Eyebrow / headline / caption — match FeatureScene look ─ */
.stage-eyebrow {
  display: inline-block;
  font-family: 'AirbnbCereal_W_Md', -apple-system, sans-serif;
  font-size: clamp(0.6rem, 1.1vw, 0.75rem);
  letter-spacing: 0.28em;
  text-transform: uppercase;
  margin-bottom: clamp(0.75rem, 1.5vw, 1.25rem);
  transition: color 700ms ease;
}
/* Scope eyebrow + headline-em colors to the text-block's OWN
   accent class (not the root's) so outgoing blocks don't flash
   into the incoming scene's color during the fade-out window.
   The text-block-level selector has higher specificity than the
   root-level one, so it wins regardless of which .accent class
   is on the parent. */
.stage-text-block.accent-teal .stage-eyebrow {
  color: #0f766e;
}
.stage-text-block.accent-blue .stage-eyebrow {
  color: #1d6fb8;
}
.stage-text-block.accent-coral .stage-eyebrow {
  color: #c2410c;
}
.stage-text-block.accent-amber .stage-eyebrow {
  color: #b45309;
}
.stage-text-block.accent-violet .stage-eyebrow {
  color: #6d28d9;
}

.stage-headline {
  font-family: 'AirbnbCereal_W_Bd', -apple-system, sans-serif;
  font-size: clamp(2rem, 7.5vw, 3rem);
  font-weight: 800;
  line-height: 1.08;
  letter-spacing: -0.035em;
  color: #0f172a;
  margin: 0 0 clamp(0.9rem, 2vw, 1.5rem);
}
@media (min-width: 900px) {
  .stage-headline {
    font-size: clamp(3rem, 3.8vw, 4.8rem);
  }
}
.stage-headline :deep(em) {
  font-style: normal;
}
.stage-text-block.accent-teal .stage-headline :deep(em) {
  background: linear-gradient(115deg, #0d9488 0%, #10b981 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.stage-text-block.accent-blue .stage-headline :deep(em) {
  background: linear-gradient(115deg, #3b82f6 0%, #0ea5e9 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.stage-text-block.accent-coral .stage-headline :deep(em) {
  background: linear-gradient(115deg, #f97316 0%, #ef4444 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.stage-text-block.accent-amber .stage-headline :deep(em) {
  background: linear-gradient(115deg, #fdad00 0%, #f97316 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.stage-text-block.accent-violet .stage-headline :deep(em) {
  background: linear-gradient(115deg, #7c3aed 0%, #a78bfa 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.stage-caption {
  font-family: 'AirbnbCereal_W_Bk', -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.4vw, 1.2rem);
  line-height: 1.6;
  color: #64748b;
  max-width: 42ch;
  margin: 0 auto;
}
/* Desktop: keep caption flush-left so longer headlines (which expand
   the text-block past the caption's max-width) don't visually offset
   the caption from the eyebrow + headline above it. */
@media (min-width: 900px) {
  .stage-text-block .stage-caption {
    margin-left: 0;
    margin-right: 0;
    max-width: 36ch;
  }
}

/* ─── Phone side — same grid stack pattern ────────────────── */
.stage-visual {
  flex-shrink: 0;
  display: grid;
  place-items: center;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  /* 3D perspective on the parent lets the mockups rotate around
     their Y axis during scene changes — incoming rotates in from
     the right, outgoing rotates away to the left. Gives the
     "feature wheel" rotation feel the user asked for. */
  perspective: 1400px;
  perspective-origin: center center;
}

/* Rotating carousel feel via TRANSITIONS (not animations).
   Inactive blocks sit at opacity 0 with the "ready to enter"
   transform. When activeIdx changes, only two blocks have
   property changes: outgoing (.active → not-active: opacity 1
   → 0, transform recoils) and incoming (not-active → .active:
   opacity 0 → 1, transform settles). Blocks that were already
   inactive and stay inactive — even if their position class
   flips between .before and .after — don't re-fire anything
   because their opacity never changed.
   Asymmetric timing: outgoing 400ms, incoming 900ms. Incoming's
   ease-out curve is slow at start so the two don't overlap
   visibly.
   3D rotation via `perspective: 1400px` on the parent. */
.stage-visual-block {
  grid-column: 1;
  grid-row: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transform: translateX(90px) rotateY(-22deg) scale(0.92);
  pointer-events: none;
  transition: opacity 400ms cubic-bezier(0.4, 0, 1, 1),
    transform 500ms cubic-bezier(0.4, 0, 1, 1);
  filter: drop-shadow(0 24px 40px rgba(0, 0, 0, 0.14))
    drop-shadow(0 8px 16px rgba(0, 0, 0, 0.09));
  will-change: opacity, transform;
  transform-style: preserve-3d;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}
/* Scenes with index < activeIdx ("before" — already viewed)
   rest on the LEFT. Scenes with index > activeIdx ("after" —
   not yet viewed) rest on the RIGHT (the default rule above).
   This index-based positioning naturally handles both scroll
   directions: on scroll-down the incoming scene was .after
   (right) → slides in from the right; on scroll-up it was
   .before (left) → slides in from the left. No scrollDir
   override needed — direction-of-travel emerges from where
   the block was sitting. */
.stage-visual-block.before {
  transform: translateX(-90px) rotateY(22deg) scale(0.92);
}
.stage-visual-block.active {
  opacity: 1;
  transform: translateX(0) rotateY(0deg) scale(1);
  pointer-events: auto;
  transition: opacity 900ms cubic-bezier(0.25, 0.1, 0.25, 1),
    transform 1100ms cubic-bezier(0.25, 0.1, 0.25, 1);
}

/* Phone zoom — mobile shares viewport height with text (column),
   desktop sits beside the text in a row so it can be bigger.
   These values are calibrated to match the landing page exactly. */
.stage-visual :deep(.iphone) {
  zoom: 0.95;
}
@media (min-width: 480px) {
  .stage-visual :deep(.iphone) {
    zoom: 1.05;
  }
}
@media (min-width: 900px) {
  .stage-visual {
    flex: 1 1 50%;
  }
  .stage-visual :deep(.iphone) {
    zoom: 1.2;
  }
}
@media (min-width: 1280px) {
  .stage-visual :deep(.iphone) {
    zoom: 1.35;
  }
}

/* Mobile: phone-FIRST, text-second — matches the landing page exactly
   (the phone sits at the top of the viewport with the headline below).
   Desktop: text-left, phone-right (default flex order). */
@media (max-width: 899px) {
  .stage-text {
    order: 2;
  }
  .stage-visual {
    order: 1;
    /* Let the phone's flex item shrink instead of clinging to its
       intrinsic 564px height. Combined with the short-viewport zoom
       breakpoints below, this keeps the headline + caption + dots
       visible even on cramped phones with the iOS toolbar showing. */
    flex-shrink: 1;
    min-height: 0;
  }
}

/* Short-viewport phone scaling — on phones where the iOS Safari toolbar
   shrinks the available height, the static `zoom: 0.95 / 1.05` was big
   enough to push the headline (and sometimes the caption) off-screen.
   Shrink proportionally so all the layout pieces fit. */
@media (max-width: 899px) and (max-height: 800px) {
  .stage-visual :deep(.iphone) {
    zoom: 0.82;
  }
}
@media (max-width: 899px) and (max-height: 720px) {
  .stage-visual :deep(.iphone) {
    zoom: 0.72;
  }
}
@media (max-width: 899px) and (max-height: 640px) {
  .stage-visual :deep(.iphone) {
    zoom: 0.62;
  }
}

@media (prefers-reduced-motion: reduce) {
  .feature-stage,
  .stage-text-block,
  .stage-visual-block,
  .stage-glow {
    transition: opacity 0.3s ease !important;
    transform: none !important;
  }
}

/* ─── Mobile compositor budget ─────────────────────────────────────
   The FeatureStage stacks 5 FeatureMockups simultaneously — desktop
   cross-fades them with opacity. On mobile, 5 composited phone
   mockup subtrees at once exceed iOS Safari's per-tab GPU budget
   and OOM the tab.

   Fix: use `visibility: hidden` on inactive blocks so the browser
   de-promotes them from the compositor (no layer allocated).
   The visibility change is delayed 650ms on exit so the fade-out
   transition still plays visually before the element drops out of
   painting; on enter it's instant so the fade-in is immediate.
   Result: at most 1 composited mockup at a time, no OOM, all
   slide-in/fade transitions intact. */
@media (max-width: 767px) {
  /* Mobile also flips visibility after the 500ms fade-out so iOS
     compositor can release the inactive phone's layer. Crucially,
     the transition shorthand MUST include the same opacity/
     transform entries as the base rule — otherwise CSS replaces
     the whole transition property and the fade/rotate stops
     working on mobile (which is what made scenes snap abruptly
     instead of cross-fade). */
  .stage-visual-block {
    visibility: hidden;
    transition: opacity 400ms cubic-bezier(0.4, 0, 1, 1),
      transform 500ms cubic-bezier(0.4, 0, 1, 1), visibility 0s linear 500ms;
  }
  .stage-visual-block.active {
    visibility: visible;
    transition: opacity 900ms cubic-bezier(0.25, 0.1, 0.25, 1),
      transform 1100ms cubic-bezier(0.25, 0.1, 0.25, 1), visibility 0s linear 0s;
  }

  /* ─── Mobile layout polish ───────────────────────────────────
     Three fixes for the tight layout on phone viewports:
     1. Increase stage-sticky top padding so the phone mockup
        clears the sticky navbar (was 3.25rem ≈ 52px; bumped to
        6rem ≈ 96px).
     2. Add padding-bottom space between the caption and the
        progress pills so they don't feel crammed.
     3. Push the progress pills closer to the very bottom edge so
        the breathing room above them expands. */
  .stage-sticky {
    padding-top: 6rem;
    padding-bottom: 5rem;
  }
  .stage-progress {
    bottom: 1.25rem;
  }
}
</style>
